import { useMemo, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Layout } from '@/components/Layout';
import { VideoGrid } from '@/components/VideoGrid';
import { FeedFilterDropdown } from '@/components/FeedFilterDropdown';
import { useVideoStream } from '@/hooks/useStreamingVideos';
import { useReportsStream } from '@/hooks/useReports';
import { useVideoFeedFilter } from '@/hooks/useVideoFeedFilter';
import { useFollowList } from '@/hooks/useFollowList';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isVideoReported } from '@/lib/reportUtils';
import {
  isYouTubeUrl,
  dedupePlebsVideos,
  PLEBS_TAG,
  VIDEO_KINDS,
  ALL_VIDEO_KINDS,
} from '@/lib/videoTypes';
import { cn } from '@/lib/utils';

type FeedTab = 'horizontal' | 'vertical' | 'notes' | 'plebs';
type Scope = 'global' | 'following';

const HORIZONTAL_KINDS = [VIDEO_KINDS.regular.horizontal, VIDEO_KINDS.addressable.horizontal];
const VERTICAL_KINDS = [VIDEO_KINDS.regular.vertical, VIDEO_KINDS.addressable.vertical];
/** Kind 1 notes are scanned client-side for linked video files. */
const NOTE_KINDS = [1];
/** Most kind 1 notes contain no video — fetch bigger batches so the
 * client-side filter still yields a decent number of note videos. */
const NOTE_BATCH_LIMIT = 200;

const PLEBS_KINDS = [...ALL_VIDEO_KINDS, 1];

/**
 * One feed tab. Horizontal and vertical videos are queried with separate
 * relay filters (kind 21/34235 vs 22/34236), so a flood of vertical videos
 * (e.g. from DiVine) can never push horizontal videos out of the relay limit
 * windows. Note videos (kind 1 with linked video files) and the plebs.app
 * archive (#pv69420, deduped across the triple-published events) are separate
 * streams too.
 */
function Feed({ tab, scope }: { tab: FeedTab; scope: Scope }) {
  const { user } = useCurrentUser();
  const { pubkeys: followedPubkeys, isLoading: followListLoading } = useFollowList(user?.pubkey);

  const kinds = tab === 'horizontal'
    ? HORIZONTAL_KINDS
    : tab === 'vertical'
      ? VERTICAL_KINDS
      : tab === 'notes'
        ? NOTE_KINDS
        : PLEBS_KINDS;
  const stream = useVideoStream({
    kinds,
    limit: tab === 'notes' ? NOTE_BATCH_LIMIT : undefined,
    tag: tab === 'plebs' ? PLEBS_TAG : undefined,
    authors: scope === 'following' && user && !followListLoading ? followedPubkeys : undefined,
    enabled: scope === 'global' || (!!user && !followListLoading),
  });

  const { filter } = useVideoFeedFilter();
  const reports = useReportsStream(true);

  const filtered = useMemo(() => {
    const visible = stream.videos.filter((video) => {
      if (!filter.showYouTube && isYouTubeUrl(video.videoUrl)) return false;
      if (!filter.showUnknownDuration && video.duration === undefined) return false;
      if (!filter.showReported && isVideoReported(video, reports)) return false;
      return true;
    });
    return tab === 'plebs' ? dedupePlebsVideos(visible) : visible;
  }, [stream.videos, filter, reports, tab]);

  if (scope === 'following') {
    if (!user) {
      return (
        <Card className="border-dashed">
          <CardContent className="py-12 px-8 text-center">
            <p className="text-muted-foreground max-w-sm mx-auto">
              Log in to see videos from people you follow.
            </p>
          </CardContent>
        </Card>
      );
    }
    if (followedPubkeys.length === 0 && !followListLoading) {
      return (
        <Card className="border-dashed">
          <CardContent className="py-12 px-8 text-center">
            <p className="text-muted-foreground max-w-sm mx-auto">
              You're not following anyone yet, or your follow list (kind 3)
              could not be found on your relays.
            </p>
          </CardContent>
        </Card>
      );
    }
  }

  const emptyMessage =
    tab === 'plebs'
      ? 'No plebs.app videos found. plebs.app uploaded each video as a regular, an addressable, and a kind 1 event with #pv69420 — surviving events are shown even when only one remains.'
      : tab === 'notes'
        ? 'No notes with video files found. Notes videos are kind 1 posts linking a video file (mp4, webm, m3u8…).'
        : scope === 'following'
          ? `No ${tab === 'horizontal' ? 'videos' : 'shorts'} from people you follow yet.`
          : `No ${tab === 'horizontal' ? 'videos' : 'shorts'} found. Try different filters or check your relay connections.`;

  return (
    <VideoGrid
      videos={filtered}
      isLoading={stream.isLoading || (scope === 'following' && followListLoading)}
      isLoadingMore={stream.isLoadingMore}
      hasMore={stream.hasMore}
      onLoadMore={stream.loadMore}
      isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
      variant={tab === 'vertical' ? 'shorts' : 'default'}
      emptyMessage={emptyMessage}
    />
  );
}

export default function Index() {
  const [tab, setTab] = useState<FeedTab>('horizontal');
  const [scope, setScope] = useState<Scope>('global');
  const { filter } = useVideoFeedFilter();

  // If note videos are filtered out while the Notes tab is active, fall back
  // to the Videos tab.
  const activeTab = tab === 'notes' && !filter.showNoteVideos ? 'horizontal' : tab;

  return (
    <Layout>
      <div className="mx-auto max-w-[2000px] px-3 sm:px-6 py-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          {/* VidStr is primarily for horizontal videos, so the "Videos" tab is
              the default. Shorts, note videos, and the plebs.app archive live
              in their own feeds. */}
          <Tabs value={activeTab} onValueChange={(v) => setTab(v as FeedTab)}>
            <TabsList>
              <TabsTrigger value="horizontal">Videos</TabsTrigger>
              <TabsTrigger value="vertical">Shorts</TabsTrigger>
              {filter.showNoteVideos && <TabsTrigger value="notes">Notes</TabsTrigger>}
              <TabsTrigger value="plebs" title="Videos uploaded via plebs.app (#pv69420)">Plebs</TabsTrigger>
            </TabsList>
          </Tabs>
          <FeedFilterDropdown />
        </div>

        {/* Scope switcher */}
        <div className="mb-5 flex gap-1.5">
          {(['global', 'following'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                scope === s
                  ? 'bg-foreground text-background'
                  : 'bg-secondary text-secondary-foreground hover:bg-accent',
              )}
            >
              {s === 'global' ? 'Global' : 'Subscribed'}
            </button>
          ))}
        </div>

        <Feed key={`${activeTab}-${scope}`} tab={activeTab} scope={scope} />
      </div>
    </Layout>
  );
}
